import { CHART_COLORS } from "@/constants/colors";

/**
 * Generates a palette of visually distinct colors in hex format.
 * This function uses HSL color space to generate colors evenly distributed across the hue wheel.
 * @param count - Number of colors to generate.
 * @returns string[] Array of hex color codes.
 */
export function generateColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    // Rotate hue, full saturation, medium lightness
    const hue = Math.round((360 / count) * i);
    colors.push(`hsl(${hue}, 70%, 55%)`);
  }
  return colors;
}

/**
 * Returns a color palette of at least `n` distinct colors.
 * Uses base palette and extends with generated HSL colors if needed.
 */
export function getColorPalette(n: number): string[] {
  if (n <= CHART_COLORS.length) return CHART_COLORS.slice(0, n);
  // Start with base, then add generated (but skip similar hues)
  const extraColors = generateColors(n - CHART_COLORS.length);
  return [...CHART_COLORS, ...extraColors];
}

/**
 * Measures the pixel width of a given text string using a specified font.
 * Useful for dynamically calculating label widths for charts or UI elements.
 *
 * @param text - The text string to be measured.
 * @param font - The CSS font descriptor (e.g., "12px"). Default is "12px".
 * @returns The width of the text in pixels.
 */
const measureCanvasTextWidth = (text: string, font = "12px"): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 10 * text.length; // Fallback if context is not available
  context.font = font;
  return context.measureText(text).width;
};

/**
 * Finds the maximum pixel width among the 'name' properties in a data array.
 * This is commonly used to determine the required width for Y-axis labels
 * in charts, ensuring that all labels are fully visible.
 *
 * @param data - Array of objects containing a 'name' property.
 * @param font - The CSS font descriptor (e.g., "12px"). Default is "12px".
 * @param maxLength - Maximum number of characters to consider for label width. If provided, labels will be truncated before measuring.
 * @returns The maximum label width (in pixels) among all 'name' values, after truncation to maxLength if specified.
 */
export const getYAxisWidth = (
  data: { name: string }[],
  font = "12px",
  maxLength?: number
): number => {
  return Math.max(
    ...data.map((item) => {
      const name = item.name;
      if (typeof maxLength === "number") {
        return name.length > maxLength
          ? measureCanvasTextWidth(`${name.slice(0, maxLength)}...`, font)
          : measureCanvasTextWidth(name.slice(0, maxLength), font);
      }
      return measureCanvasTextWidth(name, font);
    })
  );
};
